import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

type ComponentType = Tables<"component_types">;

const formSchema = z.object({
  name: z.string().min(1, "Nimi on pakollinen"),
  label: z.string().min(1, "Label on pakollinen"),
  is_mandatory: z.boolean(),
  allow_multiple: z.boolean(),
  dimension_mode: z.enum(["free", "catalog", "none"]),
});

type FormValues = z.infer<typeof formSchema>;

interface ComponentTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  componentType: ComponentType | null;
}

export function ComponentTypeDialog({
  open,
  onOpenChange,
  componentType,
}: ComponentTypeDialogProps) {
  const queryClient = useQueryClient();
  const isEdit = !!componentType;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      label: "",
      is_mandatory: false,
      allow_multiple: false,
      dimension_mode: "free",
    },
  });

  useEffect(() => {
    if (componentType) {
      form.reset({
        name: componentType.name,
        label: componentType.label,
        is_mandatory: componentType.is_mandatory,
        allow_multiple: componentType.allow_multiple,
        dimension_mode: ((componentType as { dimension_mode?: string }).dimension_mode || "free") as "free" | "catalog" | "none",
      });
    } else {
      form.reset({
        name: "",
        label: "",
        is_mandatory: false,
        allow_multiple: false,
        dimension_mode: "free",
      });
    }
  }, [componentType, form]);

  // Fetch allowed materials for this component type
  const { data: allowedMaterials = [] } = useQuery({
    queryKey: ["component_type_materials", componentType?.id],
    queryFn: async () => {
      if (!componentType?.id) return [];
      const { data, error } = await supabase
        .from("component_type_materials")
        .select("*, material:materials(*)")
        .eq("component_type_id", componentType.id)
        .order("position");
      if (error) throw error;
      return data;
    },
    enabled: !!componentType?.id,
  });

  // Fetch all materials
  const { data: allMaterials = [] } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("*")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  // Fetch attribute definitions for this component type
  const { data: attributeDefs = [] } = useQuery({
    queryKey: ["component_attribute_defs", componentType?.id],
    queryFn: async () => {
      if (!componentType?.id) return [];
      const { data, error } = await supabase
        .from("component_attribute_defs")
        .select("*, attribute:attributes(*)")
        .eq("component_type_id", componentType.id)
        .order("position");
      if (error) throw error;
      return data;
    },
    enabled: !!componentType?.id,
  });

  // Fetch all attributes
  const { data: allAttributes = [] } = useQuery({
    queryKey: ["attributes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attributes")
        .select("*")
        .is("deleted_at", null)
        .order("label");
      if (error) throw error;
      return data;
    },
  });

  // Save component type
  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isEdit) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from("component_types")
          .update({
            name: values.name,
            label: values.label,
            is_mandatory: values.is_mandatory,
            allow_multiple: values.allow_multiple,
            dimension_mode: values.dimension_mode,
          })
          .eq("id", componentType.id);
        if (error) throw error;
      } else {
        // Get max position
        const { data: maxPosRows } = await supabase
          .from("component_types")
          .select("position")
          .is("deleted_at", null)
          .order("position", { ascending: false })
          .limit(1);

        const maxPos = maxPosRows?.[0] ?? null;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any).from("component_types").insert({
          name: values.name,
          label: values.label,
          is_mandatory: values.is_mandatory,
          allow_multiple: values.allow_multiple,
          dimension_mode: values.dimension_mode,
          position: (maxPos?.position || 0) + 1,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["component_types"] });
      toast.success(isEdit ? "Komponenttityyppi päivitetty" : "Komponenttityyppi luotu");
      onOpenChange(false);
    },
    onError: () => toast.error("Tallennus epäonnistui"),
  });

  // Toggle material association
  const toggleMaterialMutation = useMutation({
    mutationFn: async ({
      materialId,
      isChecked,
    }: {
      materialId: string;
      isChecked: boolean;
    }) => {
      if (!componentType?.id) return;

      if (isChecked) {
        const { error } = await supabase.from("component_type_materials").insert({
          component_type_id: componentType.id,
          material_id: materialId,
          position: allowedMaterials.length,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("component_type_materials")
          .delete()
          .eq("component_type_id", componentType.id)
          .eq("material_id", materialId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["component_type_materials", componentType?.id],
      });
    },
    onError: () => toast.error("Päivitys epäonnistui"),
  });

  // Toggle attribute association
  const toggleAttributeMutation = useMutation({
    mutationFn: async ({
      attributeId,
      isChecked,
    }: {
      attributeId: string;
      isChecked: boolean;
    }) => {
      if (!componentType?.id) return;

      if (isChecked) {
        const { error } = await supabase.from("component_attribute_defs").insert({
          component_type_id: componentType.id,
          attribute_id: attributeId,
          position: attributeDefs.length,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("component_attribute_defs")
          .delete()
          .eq("component_type_id", componentType.id)
          .eq("attribute_id", attributeId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["component_attribute_defs", componentType?.id],
      });
    },
    onError: () => toast.error("Päivitys epäonnistui"),
  });

  const onSubmit = (values: FormValues) => {
    saveMutation.mutate(values);
  };

  const isMaterialChecked = (materialId: string) =>
    allowedMaterials.some((am) => am.material_id === materialId);

  const isAttributeChecked = (attributeId: string) =>
    attributeDefs.some((ad) => ad.attribute_id === attributeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Muokkaa komponenttityyppiä" : "Uusi komponenttityyppi"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nimi (tekninen)</FormLabel>
                    <FormControl>
                      <Input placeholder="esim. headstone" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Label (näytettävä)</FormLabel>
                    <FormControl>
                      <Input placeholder="esim. Hautakivi" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="dimension_mode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mittamoodi</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-col gap-2"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="free" id="dm-free" />
                        <Label htmlFor="dm-free" className="font-normal cursor-pointer">
                          <span className="font-medium">Vapaa mitta</span>
                          <span className="text-muted-foreground ml-2">– koko syötetään käsin (kivet)</span>
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="catalog" id="dm-catalog" />
                        <Label htmlFor="dm-catalog" className="font-normal cursor-pointer">
                          <span className="font-medium">Katalogi</span>
                          <span className="text-muted-foreground ml-2">– koko tulee attribuuteista (kehykset)</span>
                        </Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="none" id="dm-none" />
                        <Label htmlFor="dm-none" className="font-normal cursor-pointer">
                          <span className="font-medium">Ei mittoja</span>
                          <span className="text-muted-foreground ml-2">– palvelut, lisätarvikkeet</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-6">
              <FormField
                control={form.control}
                name="is_mandatory"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <Label>Pakollinen</Label>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="allow_multiple"
                render={({ field }) => (
                  <FormItem className="flex items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <Label>Useita sallittu</Label>
                  </FormItem>
                )}
              />
            </div>

            {isEdit && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Sallitut materiaalit</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {allMaterials.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        Ei materiaaleja. Luo ensin materiaaleja Materiaalit-sivulla.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {allMaterials.map((material) => (
                          <div
                            key={material.id}
                            className="flex items-center gap-2 p-2 border rounded"
                          >
                            <Checkbox
                              id={`material-${material.id}`}
                              checked={isMaterialChecked(material.id)}
                              onCheckedChange={(checked) =>
                                toggleMaterialMutation.mutate({
                                  materialId: material.id,
                                  isChecked: !!checked,
                                })
                              }
                            />
                            <Label
                              htmlFor={`material-${material.id}`}
                              className="cursor-pointer flex-1"
                            >
                              {material.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Attribuutit</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {allAttributes.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        Ei attribuutteja. Attribuutit luodaan tietokantaan.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {allAttributes.map((attr) => (
                          <div
                            key={attr.id}
                            className="flex items-center gap-2 p-2 border rounded"
                          >
                            <Checkbox
                              id={`attr-${attr.id}`}
                              checked={isAttributeChecked(attr.id)}
                              onCheckedChange={(checked) =>
                                toggleAttributeMutation.mutate({
                                  attributeId: attr.id,
                                  isChecked: !!checked,
                                })
                              }
                            />
                            <Label
                              htmlFor={`attr-${attr.id}`}
                              className="cursor-pointer flex-1"
                            >
                              <span>{attr.label}</span>
                              <span className="text-muted-foreground text-xs ml-2">
                                ({attr.attribute_type})
                              </span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Peruuta
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Tallennetaan..." : "Tallenna"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
